/**
 * This component is used to add pagination to anything else but tables
 * It was build when needed for the video settings / video gallery
 */

import React from 'react';
import PropTypes from 'prop-types';
import { config } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faFilter, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import {
  Row,
  Col,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Sort from './Sort';
import Filter from './Filter';
import DateRangeSelect from '../common/DateRangeSelect';

config.autoAddCss = false; // fix oversized icons

const paginationButtonStyle = {
  cursor: 'pointer',
  outline: 'none',
};

export const PaginationNavigation = ({ totalPages, currentPage, onPageChange }) => {
  if (totalPages === 1) {
    return null;
  }
  const paginationItems = [];
  // if we are not on the first page, add a prev button
  if (currentPage > 1) {
    paginationItems.push((
      <li
        key="prev"
        className="page-item"
        title="prev"
      >
        <span
          style={paginationButtonStyle}
          className="page-link"
          role="button"
          tabIndex={0}
          onClick={() => onPageChange(currentPage - 1)}
        > Prev
        </span>
      </li>
    ));
  }

  // make sure we have only MAXIMUM 5 pages shown in the navigator
  // the algorithm works only with odd numbers
  const displayedPagesLimit = 5;
  let startPage = 1;
  let endPage = totalPages;
  const leftRightOffset = Math.floor(displayedPagesLimit / 2);


  if ((currentPage - leftRightOffset) > 0) {
    startPage = currentPage - leftRightOffset;
  }
  if ((currentPage + leftRightOffset) < totalPages) {
    endPage = currentPage + leftRightOffset;
  }

  // now check if we really have all the page buttons displayed
  const absoluteDifference = Math.abs((endPage - startPage) - (displayedPagesLimit - 1));
  if (absoluteDifference !== 0) {
    if (endPage + absoluteDifference <= totalPages) {
      endPage += absoluteDifference;
    } else if (startPage - absoluteDifference > 0) {
      startPage -= absoluteDifference;
    }
  }

  for (let i = startPage; i <= endPage; i += 1) {
    paginationItems.push((
      <li
        key={i}
        className={`page-item ${currentPage === i ? 'active' : ''}`}
        title={`${i}`}
      >
        <span
          style={paginationButtonStyle}
          className="page-link"
          role="button"
          tabIndex={0}
          onClick={() => onPageChange(i)}
        >{ i }
        </span>
      </li>
    ));
  }
  // add a next button if we are not at the last page
  if (currentPage < totalPages) {
    paginationItems.push((
      <li
        key="next"
        className="page-item"
        title="next"
      >
        <span
          style={paginationButtonStyle}
          className="page-link"
          role="button"
          tabIndex={0}
          onClick={() => onPageChange(currentPage + 1)}
        >Next
        </span>
      </li>
    ));
  }
  return (
    <ul className="pagination">
      { paginationItems }
    </ul>
  );
};

PaginationNavigation.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

const Pagination = ({
  currentPage,
  totalItems,
  pageLimit,
  totalPages,
  onPageChange,
  onPageSizeChange,
  paginationShowsTotal,
  sortingOptions,
  onSortChange,
  formFields,
  filterDisplayed,
  onFilterButtonClick,
  onFilterApplied,
  atLeastOneFilterApplied,
  dateRange,
  onDatesSelect,
}) => {
  const renderPageSizeSelector = () => (
    <UncontrolledDropdown>
      <DropdownToggle color="primary" outline caret>
        {pageLimit}
      </DropdownToggle>
      <DropdownMenu>
        {[10, 25, 50, 100].map(v => (
          <DropdownItem
            key={`page-size-item-${v}`}
            active={v === pageLimit}
            onClick={() => onPageSizeChange(v)}
          >
            {v}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  const renderSortBy = () => (
    <div className="sort-button">
      { sortingOptions.map(sortProperty => (
        <Sort
          sortData={{ ...sortProperty }}
          onSortChange={onSortChange}
          key={sortProperty.sortBy}
        />
      ))
      }

    </div>
  );


  /**
   * This is a callback which will render the text from outside of the component
   * @returns {*}
   */
  const renderCurrentRange = () => {
    let start = 0;
    let end = 0;
    if (pageLimit === Number.MAX_SAFE_INTEGER) {
      start = 1;
      end = totalItems;
    } else {
      start = ((currentPage - 1) * pageLimit) + 1;
      end = currentPage * pageLimit;
      if (end > totalItems) {
        end = totalItems;
      }
    }
    return paginationShowsTotal(start, end, totalItems);
  };

  const renderFilter = () => {
    if (formFields.length) {
      const appliedFilterClass = atLeastOneFilterApplied === true ? 'filterApplied' : '';
      return (
        <button
          className={'filter-button ' +
          `${appliedFilterClass} ${filterDisplayed ? 'filter-displayed' : ''}`}
          onClick={() => {
          onFilterButtonClick();
        }}
        >

          <span className="filter-text">
            {filterDisplayed ? 'filter' : ''}
          </span>

          <FA
            icon={filterDisplayed ? faChevronUp : faFilter}
            className={`
              panorame-${atLeastOneFilterApplied && !filterDisplayed ? 'pink' : 'green'}
              ${filterDisplayed ? 'ml-1' : ''}
            `}
            fixedWidth
          />
        </button>
      );
    }
    return <span />;
  };

  return (
    <Row className="pagination-main-container">
      <Col
        md={{ size: 3, order: 1 }}
        xs={{ size: 6, order: 1 }}
        align="right"
        className="range-and-filter-container"
      >
        { renderCurrentRange() }
        &nbsp;
        { renderFilter() }
      </Col>
      <Col
        md={{ size: 6, order: 2 }}
        sm={{ size: 7, order: 4 }}
        xs={{ size: 7, order: 4 }}
        className="date-range-select-container"
        align="center"
      >
        <DateRangeSelect
          placeholder="Date Range"
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={onDatesSelect}
          timePicker
        />
        {!!(dateRange.startDate || dateRange.endDate) &&
          <Button
            className="ml-2"
            color="primary"
            outline
            onClick={() => onDatesSelect({})}
          >
            Clear
          </Button>
        }
      </Col>
      <Col
        md={{ size: 3, order: 3 }}
        xs={{ size: 6, order: 2 }}
        align="right"
        className="page-sort-by-container"
      >
        { renderSortBy() }
      </Col>
      <Col
        md={{ size: 4, order: 5 }}
        sm={{ size: 5, order: 5 }}
        xs={{ size: 5, order: 5 }}
        align="left"
        className="page-size-container"
      >
        <span className="page-size-label">Page Size:</span>
        { renderPageSizeSelector() }
      </Col>
      <Col
        md={{ size: 8, order: 6 }}
        xs={{ size: 12, order: 6 }}
        align="right"
        className="page-items-container"
      >
        { PaginationNavigation({ totalPages, currentPage, onPageChange }) }
      </Col>
      <Col
        md={{ size: 12, order: 4 }}
        sm={{ size: 12, order: 3 }}
        xs={{ size: 12, order: 3 }}
      >
        <Filter
          show={filterDisplayed}
          formFields={formFields}
          atLeastOneFilterApplied={onFilterApplied}
        />
      </Col>
    </Row>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  pageLimit: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  paginationShowsTotal: PropTypes.func.isRequired,
  sortingOptions: PropTypes.array,
  onSortChange: PropTypes.func.isRequired,
  formFields: PropTypes.array,
  filterDisplayed: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.string,
  ]),
  onFilterButtonClick: PropTypes.func.isRequired,
  onFilterApplied: PropTypes.func.isRequired,
  atLeastOneFilterApplied: PropTypes.bool.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
    endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
  }).isRequired,
  onDatesSelect: PropTypes.func.isRequired,
};

Pagination.defaultProps = {
  sortingOptions: [],
  formFields: [],
  filterDisplayed: false,
};

export default Pagination;



// WEBPACK FOOTER //
// ./src/client/components/common/Pagination.js